import { Component, Inject, OnDestroy, Renderer2, DOCUMENT } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: false
})
export class SidebarComponent implements OnDestroy {

  /** Whether the mobile "more" slide-up popup is currently open. */
  public isMenuOpen: boolean = false;

  /**
   * Whether the "more" button on the mobile bottom bar should render as active.
   * True while the active route is a popup-only item (rows 2-3) that has no
   * dedicated icon on the bottom bar.
   */
  public isMoreActive: boolean = false;

  /**
   * Routes reachable only from inside the "more" popup (no bottom-bar icon).
   * When the active route matches one of these, the "more" button stays active.
   */
  private readonly moreRoutes: string[] = [
    '/manage-user',
    '/requested-withdrawal-wallets',
    '/manage-loans',
    '/deposit-assets',
    '/manage-delivery',
    '/royalty',
    '/announcement-detail'
  ];

  /**
   * @param {Document} document - The DOM document, used to lock body scroll while the popup is open.
   * @param {Renderer2} renderer - Angular renderer used to toggle the body scroll-lock class.
   * @param {Router} router - Angular router used to keep the "more" button active state in sync with navigation.
   */
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private router: Router
  ) {
    this.updateMoreActive(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe((event: NavigationEnd) => this.updateMoreActive(event.urlAfterRedirects));
  }

  /**
   * Recomputes whether the "more" button should be active for the given URL.
   * @param {string} url - The current router URL (may include query params/fragments).
   * @returns {void}
   */
  private updateMoreActive(url: string): void {
    const path = url.split(/[?#]/)[0];
    this.isMoreActive = this.moreRoutes.some(
      route => path === route || path.startsWith(route + '/')
    );
  }

  /**
   * Opens the mobile navigation popup and locks background scrolling.
   * @returns {void}
   */
  public openMenu(): void {
    this.isMenuOpen = true;
    this.setScrollLock(true);
  }

  /**
   * Closes the mobile navigation popup and restores background scrolling.
   * @returns {void}
   */
  public closeMenu(): void {
    this.isMenuOpen = false;
    this.setScrollLock(false);
  }

  /**
   * Toggles the body scroll-lock class so the main page cannot scroll while the popup is open.
   * @param {boolean} lock - True to prevent page scroll, false to restore it.
   * @returns {void}
   */
  private setScrollLock(lock: boolean): void {
    if (lock) {
      this.renderer.addClass(this.document.body, 'mobile-nav-open');
    } else {
      this.renderer.removeClass(this.document.body, 'mobile-nav-open');
    }
  }

  /**
   * Ensures the scroll lock is released if the component is destroyed while the popup is open.
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.setScrollLock(false);
  }

}
